/** @jsxImportSource @emotion/react */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Button, { ButtonGroup } from '@atlaskit/button';
import { PageWrapper } from 'components/page';

import PageHeader from '@atlaskit/page-header';
import Page from '@atlaskit/page';
import { jsx, css } from '@emotion/react';
import { FormattedMessage, useIntl } from 'components/i18n';
import { ArrayParam, NumberParam, StringParam, useQueryParam, useQueryParams, withDefault } from "use-query-params";
import { DropdownLinkItem, MoreDropdownMenu } from 'components/dropdown-menu';
import { DropdownItem, DropdownItemGroup } from '@atlaskit/dropdown-menu';
import { useQuery, useMutation, queryCache } from 'react-query';
import BankAccountsTable from './components/BankAccountsTable';
import KlarnaAppLauncher, {
  startKlarnaFlow,
} from './components/KlarnaAppLauncher';
import BankAccountModal from './components/BankAccountModal';
import { BankAccount } from './types';
import api from '../../libs/api';
import { useNotifications } from '../../components/notification';
import useConfirm from '../../components/modal-dialog/useConfirm';
import Tabs, { Tab, TabList, TabPanel } from "@atlaskit/tabs";


// const breadcrumbs = (
//   <BreadcrumbsStateless onExpand={() => {}}>
//     <BreadcrumbsItem text="Bank accounts" key="Bank accounts" />
//     <BreadcrumbsItem text="NL49ABNA012345678" key="NL49ABNA012345678" />
//   </BreadcrumbsStateless>
// );

const fetchBankAccounts = async () =>
  api.get('/BankAccounts', {
    params: { filter: { include: 'klarnaConnection' } },
  });

const saveBankAccount = async (bankAccount: BankAccount) =>
  api.put('/BankAccounts', bankAccount);

const deleteBankAccount = async (id: string) =>
  api.delete(`/BankAccounts/${id}`);

const refreshKlarnaConnection = async (klarnaConnectionId: string) =>
  api.post(`/klarna-connections/${klarnaConnectionId}/refresh`);

const REFETCH_INTERVAL = 5000; // milliseconds

const BankAccountsPage = () => {
  const [queryParams, setQueryParams] = useQueryParams({
    status: withDefault(ArrayParam, ['active']),
  });
  const handleChangeQueryParam = useCallback((param: string) => (value: any) => {
    setQueryParams({[param]: value || undefined})
  } ,[]);

  const { status } = queryParams;
  // const { error, data, loading } = useQuery(BANK_ACCOUNTS_QUERY, { fetchPolicy: "cache-and-network", notifyOnNetworkStatusChange: true,});
  const { data, isLoading, refetch } = useQuery(['bankAccounts'], fetchBankAccounts, {});

  useEffect(() => {
    const interval = setInterval(() => {
      refetch();
    }, REFETCH_INTERVAL);
    return () => clearInterval(interval);
  }, []);

  const activeBankAccounts = useMemo(()=> (data||[]).filter((b:BankAccount)=>b.isActive),[data]);
  const inactiveBankAccounts = useMemo(()=>(data||[]).filter((b:BankAccount)=>!b.isActive),[data]);
  const hasInactiveBankAccounts = inactiveBankAccounts.length > 0;

  const { showSuccessNotification, showErrorNotification } = useNotifications();
  const { confirmDelete } = useConfirm();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [
    currentBankAccount,
    setCurrentBankAccount,
  ] = useState<BankAccount | null>();

  const [save, { status: saveBankAccountStatus, isLoading: isSaving }] = useMutation(saveBankAccount);
    // onMutate,
    // onSuccess,
    // onError,
    // onSettled,
    // throwOnError,
    // useErrorBoundary,

  const handleModalSave = useCallback(
    async (bankAccount: BankAccount) => {
      await save(bankAccount, {
        onSuccess: () => {
          setIsModalOpen(false);
          queryCache.invalidateQueries('bankAccounts');
          showSuccessNotification(
            bankAccount.id?
            <FormattedMessage
              id="bank.BankAccountsPage.notifications.save.success.title`"
              defaultMessage="Bank account saved"
            /> :
            <FormattedMessage
              id="bank.BankAccountsPage.notifications.created.success.title`"
              defaultMessage="Bank account added"
            />);
        },
        onError: (error: any) => {
          showErrorNotification(
            //   <FormattedMessage
            //   id='bank.BankAccountsPage.notifications.save.error.title`'
            //   defaultMessage='Bank account not saved'
            // />,
            error.message
          )}
    })},
    [setIsModalOpen]
  );
  const handleModalCancel = useCallback(() => {
    setIsModalOpen(false);
  }, [setIsModalOpen]);

  const handleEdit = useCallback(async (bankAccount) => {
    setCurrentBankAccount(bankAccount);
    setIsModalOpen(true);
  }, []);

  const handleAddCustomAccount = useCallback(async () => {
    setCurrentBankAccount(null);
    setIsModalOpen(true);
  }, []);

  const handleRefreshBankData = useCallback(
    async (bankAccount: BankAccount) => {
      const klarnaConnectionId = bankAccount.klarnaConnection?.id;
      queryCache.setQueryData(['bankAccounts'], (bankAccounts: BankAccount[]) =>
        bankAccounts?.map((account) => {
          if (account.klarnaConnection?.id !== klarnaConnectionId)
            return account;
          return {
            ...account,
            klarnaConnection: {
              ...account.klarnaConnection,
              isRefreshing: true,
            },
          };
        })
      );
      try {
        const result = await refreshKlarnaConnection(
          bankAccount.klarnaConnection?.id
        );
        showSuccessNotification(
            <FormattedMessage
              id="bank.BankAccountsPage.notifications.refreshedTransactions.success.title`"
              defaultMessage="Refreshed bank transactions"
            />);
        console.log('klarna refresh result', result);

      } catch (e) {
        alert(e.message);
      } finally {
        queryCache.invalidateQueries('bankAccounts');
      }
    },
    []
  );

  const handleRemove = useCallback(async (bankAccount: BankAccount) => {
    confirmDelete({
      title: (
        <FormattedMessage
          id="bank.BankAccountsPage.modals.delete.title`"
          defaultMessage="Delete this bank account?"
        />
      ),
      content: (
        <FormattedMessage
          id="bank.BankAccountsPage.modals.delete.content`"
          defaultMessage="You are about to delete bank account {bankAccountNumber}."
          values={{ bankAccountNumber: bankAccount.bankAccountNumber }}
        />
      ),
      onConfirm: async () => {
        try {
          await deleteBankAccount(bankAccount.id);
          showSuccessNotification(
            <FormattedMessage
              id="bank.BankAccountsPage.notifications.delete.success.title`"
              defaultMessage="Bank account deleted"
            />
          );
        } catch (e) {
          showErrorNotification(e.message);
        }
        queryCache.invalidateQueries('bankAccounts');
      },
    });
  }, []);

  const intl = useIntl();
  const connectKlarna = useCallback(
    async (bankCode?: string) => {
      try {
        const klarnaConnection = await startKlarnaFlow(bankCode, intl.locale as 'nl' | 'en');
        showSuccessNotification(
          <FormattedMessage
            id="bank.BankAccountsPage.notifications.klarna.success.title`"
            defaultMessage="Bank account connected with Klarna"
        />);
      }catch(error) {
        if(error && error.message) {
          showErrorNotification(error.message);
        }
        console.log('error connecting klarna');
      }
    },
    [intl.locale]
  );

  const handleReconnect = useCallback((bankAccount: BankAccount) => {
    connectKlarna(bankAccount.bank.code);
  }, []);

  const actionsContent = useMemo(
    () => (
      <ButtonGroup>
        <Button onClick={() => connectKlarna()}>
          <FormattedMessage
            id="bank.BankAccountsPage.add"
            defaultMessage="Add account"
          />
        </Button>
        <MoreDropdownMenu>
          <DropdownItemGroup>
            <DropdownItem onClick={handleAddCustomAccount}>
              <FormattedMessage
                id="bank.BankAccountsPage.addCustom"
                defaultMessage="Add custom account"
              />
            </DropdownItem>
            <DropdownLinkItem to="../bank/rules">
              <FormattedMessage
                id="bank.BankAccountsPage.bankRules"
                defaultMessage="Bank rules"
              />
            </DropdownLinkItem>
          </DropdownItemGroup>
        </MoreDropdownMenu>
      </ButtonGroup>
    ),
    []
  );

  // const barContent = React.useMemo(() => {
  //   const itemsContainer = css`
  //     display: flex;
  //     align-items: center;
  //     margin-top: 1rem;
  //     margin-bottom: 1rem;
  //     && > div:not(:first-of-type) {
  //       margin-left: 8px;
  //     }
  //   `;
  //
  //   return (
  //     <div>
  //       <div css={itemsContainer}>
  //         <div css={css`flex: 0 0 100px;`}>
  //           <BankAccountStatusFilter value={status} onChange={handleChangeQueryParam('status')} />
  //         </div>
  //         {/*<div>{isFetching && <Spinner/>}</div>*/}
  //       </div>
  //     </div>
  //   );
  // }, [intl, status]);

  return (
    <PageWrapper>
      <Page>
        <KlarnaAppLauncher />
        <PageHeader
          actions={actionsContent}>
          <FormattedMessage
            id="bank.BankAccountsPage.title"
            defaultMessage="Bank accounts"
          />
        </PageHeader>

        {hasInactiveBankAccounts ?
          <Tabs>
            <TabList>
              <Tab><FormattedMessage
                id='bank.BankAccountsPage.active'
                defaultMessage='Active' />
              </Tab>
              <Tab><FormattedMessage
                id='bank.BankAccountsPage.inactive'
                defaultMessage='Inactive' />
              </Tab>
            </TabList>
            <TabPanel>
              <BankAccountsTable
                bankAccounts={activeBankAccounts}
                isLoading={isLoading && !data}
                onEdit={handleEdit}
                onRemove={handleRemove}
                onReconnect={handleReconnect}
                onRefreshBankData={handleRefreshBankData}
              />
            </TabPanel>
            <TabPanel>
              <BankAccountsTable
                bankAccounts={inactiveBankAccounts}
                isLoading={isLoading && !data}
                onEdit={handleEdit}
                onRemove={handleRemove}
                onReconnect={handleReconnect}
                onRefreshBankData={handleRefreshBankData}
              />
            </TabPanel>
          </Tabs> :
          <BankAccountsTable
            bankAccounts={data}
            isLoading={isLoading && !data}
            onEdit={handleEdit}
            onRemove={handleRemove}
            onReconnect={handleReconnect}
            onRefreshBankData={handleRefreshBankData}
          />
        }

        {isModalOpen && (
          <BankAccountModal
            isOpen={isModalOpen}
            onCancel={handleModalCancel}
            onSave={handleModalSave}
            isSaving={isSaving}
            bankAccount={currentBankAccount}
          />
        )}
      </Page>
    </PageWrapper>
  );
};

export default BankAccountsPage;
